import {
  AirHandlerTypeCodeType,
  HyperHeatFilterValueType,
  SystemTypeFilterValueType
} from 'data/types';
import { EquipmentFilterState } from 'features/ui';
import {
  FilterReconcileMemory,
  memorizedUniverseFromMemory,
  SystemListFilterState,
  SystemListFilterUniverse
} from './equipment-comparison-filters';

function setToArrayOrNull<T>(
  set: Set<T>,
  universe: ReadonlyArray<T>
): T[] | null {
  if (set.size === 0) {
    return null;
  }
  if (set.size === universe.length && universe.every((v) => set.has(v))) {
    return null;
  }
  return Array.from(set);
}

// Persisted configIds can reference configurations that no longer exist
// ("Configurations reset" regenerates uniqueIds), so survivors are the
// intersection with the live ids and an all-dead selection falls back to
// all live configs.
export function reconcileConfigIds(
  persisted: ReadonlyArray<string> | null,
  liveConfigIds: ReadonlyArray<string>
): Set<string> {
  if (persisted === null) {
    return new Set(liveConfigIds);
  }
  const live = new Set(liveConfigIds);
  const survivors = new Set(persisted.filter((id) => live.has(id)));
  return survivors.size === 0 ? live : survivors;
}

export function equipmentFiltersToFilterState(
  filters: EquipmentFilterState,
  memorizedUniverse: SystemListFilterUniverse,
  liveConfigIds: ReadonlyArray<string>
): SystemListFilterState {
  return {
    configIds: reconcileConfigIds(filters.configIds, liveConfigIds),
    airHandlerTypes:
      filters.airHandlerTypes === null
        ? new Set<AirHandlerTypeCodeType>(memorizedUniverse.airHandlerTypes)
        : new Set<AirHandlerTypeCodeType>(filters.airHandlerTypes),
    condenserSeries:
      filters.condenserSeries === null
        ? new Set<string>(memorizedUniverse.condenserSeries)
        : new Set<string>(filters.condenserSeries),
    condenserBtus:
      filters.condenserBtus === null
        ? new Set<number>(memorizedUniverse.condenserBtus)
        : new Set<number>(filters.condenserBtus),
    hyperHeat:
      filters.hyperHeat === null
        ? new Set<HyperHeatFilterValueType>(memorizedUniverse.hyperHeat)
        : new Set<HyperHeatFilterValueType>(filters.hyperHeat),
    systemTypes:
      filters.systemTypes === null
        ? new Set<SystemTypeFilterValueType>(memorizedUniverse.systemTypes)
        : new Set<SystemTypeFilterValueType>(filters.systemTypes),
    refrigerants:
      filters.refrigerants === null
        ? new Set<string>(memorizedUniverse.refrigerants)
        : new Set<string>(filters.refrigerants)
  };
}

// Serializes one dimension against everything ever seen (knownValues), not
// just the displayed universe. Hidden values (known but not displayed) keep
// the checked state they had in prevChecked, so unchecking a configuration
// and re-checking it later restores its options' prior state, and the
// null-collapse only fires when every known value is checked. Iterating
// knownValues (sorted) keeps persisted arrays sorted for stable
// request-body hashes downstream.
function serializeDimension<T>(
  visibleChecked: ReadonlySet<T>,
  displayedValues: ReadonlyArray<T>,
  knownValues: ReadonlyArray<T>,
  prevChecked: ReadonlyArray<T> | null
): T[] | null {
  const displayed = new Set(displayedValues);
  const prev = prevChecked === null ? null : new Set(prevChecked);
  const checked = new Set<T>();
  knownValues.forEach((value) => {
    if (displayed.has(value)) {
      if (visibleChecked.has(value)) checked.add(value);
    } else if (prev === null || prev.has(value)) {
      checked.add(value);
    }
  });
  return setToArrayOrNull(checked, knownValues);
}

export function filterStateToEquipmentFilters(
  state: SystemListFilterState,
  memorizedUniverse: SystemListFilterUniverse,
  memory: FilterReconcileMemory,
  prevFilters: EquipmentFilterState
): EquipmentFilterState {
  const knownValues = memorizedUniverseFromMemory(memory);
  return {
    configIds: setToArrayOrNull(state.configIds, Array.from(memory.configIds)),
    airHandlerTypes: serializeDimension(
      state.airHandlerTypes,
      memorizedUniverse.airHandlerTypes,
      knownValues.airHandlerTypes,
      prevFilters.airHandlerTypes
    ),
    condenserSeries: serializeDimension(
      state.condenserSeries,
      memorizedUniverse.condenserSeries,
      knownValues.condenserSeries,
      prevFilters.condenserSeries
    ),
    condenserBtus: serializeDimension(
      state.condenserBtus,
      memorizedUniverse.condenserBtus,
      knownValues.condenserBtus,
      prevFilters.condenserBtus
    ),
    hyperHeat: serializeDimension(
      state.hyperHeat,
      memorizedUniverse.hyperHeat,
      knownValues.hyperHeat,
      prevFilters.hyperHeat
    ),
    systemTypes: serializeDimension(
      state.systemTypes,
      memorizedUniverse.systemTypes,
      knownValues.systemTypes,
      prevFilters.systemTypes
    ),
    refrigerants: serializeDimension(
      state.refrigerants,
      memorizedUniverse.refrigerants,
      knownValues.refrigerants,
      prevFilters.refrigerants
    )
  };
}
